import { type CSSProperties } from "react";
import { useNavigate } from "react-router-dom";
import {
  RefreshCw,
  Receipt,
  CheckCircle2,
  Users as UsersIcon,
  ChevronRight,
} from "lucide-react";
import type { SectionStatus } from "../overviewTypes";

/**
 * "Where things stand" row across the 4 operational Accounting sub-pages.
 *
 * Responsive:
 *   desktop (>960px): repeat(4, 1fr) with vertical hairline dividers
 *   tablet (641-960px): 2x2 grid, dividers between
 *   phone (<=640px): 1 column stack, horizontal dividers between rows
 */

interface SectionStatusRowProps {
  status: SectionStatus;
}

const PALETTE = {
  ink: "#0E1A1A",
  ink3: "#3A4A52",
  ink4: "#6B7684",
  lineSoft: "#ECEFF3",
  teal: "#0F6E56",
  amber: "#B45309",
};

function formatDate(iso: string | null): string {
  if (!iso) return "never";
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString("en-CA", { month: "short", day: "numeric" });
}

export function SectionStatusRow({ status }: SectionStatusRowProps) {
  const navigate = useNavigate();

  const cols = [
    {
      key: "tx",
      icon: <RefreshCw size={15} strokeWidth={1.8} />,
      title: "Bank transactions",
      body: (
        <>
          <strong>{status.transactions.importedThisMonth}</strong> imported this month.{" "}
          {status.transactions.needReview > 0 && (
            <span style={{ color: PALETTE.amber, fontWeight: 600 }}>
              {status.transactions.needReview} need review.
            </span>
          )}
        </>
      ),
      route: "/accounting/bank-transactions",
    },
    {
      key: "rc",
      icon: <Receipt size={15} strokeWidth={1.8} />,
      title: "Receipts",
      body: (
        <>
          <strong>{status.receipts.matchedThisMonth}</strong> matched this month.{" "}
          {status.receipts.missing > 0 && (
            <span style={{ color: PALETTE.amber, fontWeight: 600 }}>
              {status.receipts.missing} missing.
            </span>
          )}
        </>
      ),
      route: "/accounting/receipts",
    },
    {
      key: "re",
      icon: <CheckCircle2 size={15} strokeWidth={1.8} />,
      title: "Reconcile",
      body: (
        <>
          Last done <strong>{formatDate(status.reconcile.lastDoneDate)}</strong>.{" "}
          {status.reconcile.accountsOverdue > 0 && (
            <span style={{ color: PALETTE.amber, fontWeight: 600 }}>
              {status.reconcile.accountsOverdue} account overdue.
            </span>
          )}
        </>
      ),
      route: "/accounting/reconcile",
    },
    {
      key: "ac",
      icon: <UsersIcon size={15} strokeWidth={1.8} />,
      title: "My accountant",
      body:
        status.accountant.state === "pending" && status.accountant.inviteeName ? (
          <>
            <strong>{status.accountant.inviteeName}</strong> invited {formatDate(status.accountant.invitedDate)}.{" "}
            <span style={{ color: PALETTE.teal, fontWeight: 600 }}>Pending acceptance.</span>
          </>
        ) : status.accountant.state === "accepted" && status.accountant.inviteeName ? (
          <>
            <strong>{status.accountant.inviteeName}</strong> is connected.
          </>
        ) : (
          <>No accountant invited.</>
        ),
      route: "/accounting/my-accountant",
    },
  ];

  return (
    <section style={sectionStyle}>
      <p style={sectionLabelStyle}>Where things stand</p>
      <div className="novala-status-row" style={rowStyle}>
        {cols.map((col, i) => (
          <div
            key={col.key}
            className="novala-status-col"
            style={{
              ...colStyle,
              paddingLeft: i === 0 ? 0 : 24,
              paddingRight: i === cols.length - 1 ? 0 : 24,
              borderRight: i === cols.length - 1 ? "none" : `1px solid ${PALETTE.lineSoft}`,
            }}
          >
            <div style={titleRowStyle}>
              <span style={icoStyle}>{col.icon}</span>
              <p style={titleStyle}>{col.title}</p>
            </div>
            <p style={bodyStyle}>{col.body}</p>
            <button type="button" style={linkStyle} onClick={() => navigate(col.route)}>
              Open <ChevronRight size={10} strokeWidth={2.5} />
            </button>
          </div>
        ))}
      </div>
      <style>{`
        @media (max-width: 640px) {
          .novala-status-row {
            grid-template-columns: 1fr !important;
          }
          .novala-status-col {
            padding: 12px 0 !important;
            border-right: 0 !important;
            border-bottom: 1px solid ${PALETTE.lineSoft};
          }
          .novala-status-col:last-child { border-bottom: 0 !important; }
        }
        @media (min-width: 641px) and (max-width: 960px) {
          .novala-status-row {
            grid-template-columns: repeat(2, 1fr) !important;
          }
          .novala-status-col {
            padding: 12px 20px !important;
          }
          .novala-status-col:nth-child(odd) { border-right: 1px solid ${PALETTE.lineSoft} !important; padding-left: 0 !important; }
          .novala-status-col:nth-child(even) { border-right: 0 !important; padding-right: 0 !important; }
          .novala-status-col:nth-child(-n+2) { border-bottom: 1px solid ${PALETTE.lineSoft}; }
        }
      `}</style>
    </section>
  );
}

const sectionStyle: CSSProperties = { marginBottom: 20 };

const sectionLabelStyle: CSSProperties = {
  fontSize: 11.5, fontWeight: 700, letterSpacing: "0.08em",
  textTransform: "uppercase", color: PALETTE.ink3, margin: "0 0 14px",
};

const rowStyle: CSSProperties = {
  display: "grid", gridTemplateColumns: "repeat(4, 1fr)",
};

const colStyle: CSSProperties = {
  padding: "4px 24px 0",
};

const titleRowStyle: CSSProperties = {
  display: "flex", alignItems: "center", gap: 8, margin: "0 0 6px",
};

const icoStyle: CSSProperties = {
  color: PALETTE.ink3,
  display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
};

const titleStyle: CSSProperties = {
  fontSize: 13, fontWeight: 700, color: PALETTE.ink, margin: 0,
};

const bodyStyle: CSSProperties = {
  fontSize: 11.5, color: PALETTE.ink3, lineHeight: 1.55, margin: "0 0 8px",
};

const linkStyle: CSSProperties = {
  background: "transparent", border: 0, color: PALETTE.teal,
  fontSize: 12, fontWeight: 600, cursor: "pointer", padding: 0,
  fontFamily: "inherit", display: "inline-flex", alignItems: "center", gap: 3,
};